import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import TerminalOutlinedIcon from '@mui/icons-material/TerminalOutlined';
import { Box, Button, Stack, Step, StepButton, Stepper, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';

import CheckForNewUploads from './CheckForNewUploads';
import { buildItEmailBody, getItEmailSubject, getItNotes } from './cliInstructionItNotes';
import { CLIInstructionsProps } from './cliInstructionTypes';
import ItEmailDialog from './ItEmailDialog';
import { useUploadSlidesOpenedAt } from './UploadSlidesOpenedAtContext';
import { buildCliInstructionSteps } from './useCliInstructionSteps';

const CLIInstructions = ({
  configureStep,
  profileStep,
  credentialMode = 'temporary-nucleai',
  connectStepContent,
  initialStepIndex = 0,
  itNotesDestination,
  selectedOs,
  setSelectedOs,
  defaultOperatingSystem,
  readyToUpload = false,
  studyId,
  title,
  uploadStepContent,
  uploadStepTitle,
  validateStep,
}: CLIInstructionsProps) => {
  const [activeStep, setActiveStep] = useState(initialStepIndex);
  const [isSetupOpen, setIsSetupOpen] = useState(false);
  const [isItEmailDialogOpen, setIsItEmailDialogOpen] = useState(false);
  const [isCheckingForNewUploads, setIsCheckingForNewUploads] = useState(false);
  const openedAt = useUploadSlidesOpenedAt();

  useEffect(() => {
    setActiveStep(initialStepIndex);
  }, [initialStepIndex]);

  const itNotes = getItNotes(credentialMode, itNotesDestination);
  const itEmailSubject = getItEmailSubject(credentialMode);
  const itEmailBody = buildItEmailBody(itNotes, credentialMode);
  const mailtoHref = `mailto:?subject=${encodeURIComponent(itEmailSubject)}&body=${encodeURIComponent(itEmailBody)}`;
  const steps = buildCliInstructionSteps({
    profileStep,
    configureStep,
    connectStepContent,
    itNotes,
    onOpenSummary: () => setIsItEmailDialogOpen(true),
    selectedOs,
    defaultOperatingSystem,
    setSelectedOs,
    uploadStepContent,
    uploadStepTitle,
    validateStep,
  });
  const setupSteps = steps.slice(0, -1);
  const uploadStep = steps[steps.length - 1];
  const activeStepConfig = setupSteps[activeStep];
  const firstIncompleteStepIndex = setupSteps.findIndex((step) => step.isReady === false);
  const isCurrentStepBlocked = firstIncompleteStepIndex !== -1 && activeStep >= firstIncompleteStepIndex;
  const isFirstStep = activeStep === 0;
  const isLastStep = activeStep === setupSteps.length - 1;
  const showStartUploading = isLastStep && readyToUpload;
  const canCheckForNewUploads = Boolean(studyId) && openedAt !== null;

  const showCheckScreen = isCheckingForNewUploads && canCheckForNewUploads;
  const setupTitle = credentialMode === 'customer-aws' ? 'Set up S3-to-S3 access' : 'Set up computer uploads';

  return (
    <>
      {showCheckScreen && (
        <CheckForNewUploads
          studyId={studyId as string}
          openedAt={openedAt as number}
          onBack={() => setIsCheckingForNewUploads(false)}
        />
      )}
      {isSetupOpen && !showCheckScreen ? (
        <Stack data-testid="cli-instructions" spacing={2} sx={{ px: 2, py: 1.5 }}>
          <Typography component="h2" variant="h6">
            {setupTitle}
          </Typography>
          <Stepper data-testid="cli-instructions-stepper" nonLinear activeStep={activeStep} alternativeLabel>
            {setupSteps.map((step, index) => {
              const isLocked = firstIncompleteStepIndex !== -1 && index > firstIncompleteStepIndex;
              return (
                <Step key={step.title} completed={step.isReady}>
                  <StepButton color="inherit" disabled={isLocked} onClick={() => setActiveStep(index)}>
                    {step.title}
                  </StepButton>
                </Step>
              );
            })}
          </Stepper>
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              {activeStepConfig.icon}
              <Typography variant="subtitle2">{`${activeStep + 1}. ${activeStepConfig.title}`}</Typography>
            </Stack>
            {activeStepConfig.content}
          </Stack>
          <Stack
            direction="row"
            spacing={1.5}
            justifyContent="space-between"
            sx={(theme) => ({
              position: 'sticky',
              bottom: 0,
              zIndex: 1,
              pt: 2,
              backgroundColor: theme.palette.background.paper,
            })}
          >
            {showStartUploading ? (
              <Box />
            ) : (
              <Button color="inherit" onClick={() => setIsSetupOpen(false)}>
                Back to upload
              </Button>
            )}
            <Stack spacing={0.5} alignItems="flex-end">
              <Stack direction="row" spacing={1}>
                <Button
                  color="inherit"
                  startIcon={<ArrowBackOutlinedIcon />}
                  disabled={isFirstStep}
                  onClick={() => setActiveStep(activeStep - 1)}
                >
                  Back
                </Button>
                {showStartUploading ? (
                  <Button variant="contained" color="primary" onClick={() => setIsSetupOpen(false)}>
                    Start uploading
                  </Button>
                ) : !isLastStep ? (
                  <Button
                    variant="outlined"
                    color="secondary"
                    endIcon={<ArrowForwardOutlinedIcon />}
                    disabled={isCurrentStepBlocked}
                    onClick={() => setActiveStep(activeStep + 1)}
                  >
                    Next
                  </Button>
                ) : null}
              </Stack>
              {isCurrentStepBlocked && activeStepConfig.continueBlockedReason ? (
                <Typography variant="caption" color="text.secondary">
                  {activeStepConfig.continueBlockedReason}
                </Typography>
              ) : null}
            </Stack>
          </Stack>
        </Stack>
      ) : null}
      <Box
        data-testid="cli-instructions-upload"
        sx={{
          px: 2,
          py: 1.5,
          display: showCheckScreen || isSetupOpen ? 'none' : undefined,
        }}
      >
        <Stack
          data-testid="cli-instructions-sticky-top"
          spacing={2}
          sx={(theme) => ({
            position: 'sticky',
            top: 0,
            zIndex: 1,
            flexShrink: 0,
            pb: 1.5,
            backgroundColor: theme.palette.background.paper,
          })}
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <TerminalOutlinedIcon color="primary" fontSize="small" />
              <Typography variant="subtitle2">{title}</Typography>
            </Stack>
          </Box>
        </Stack>

        {uploadStep.content}
        <Stack
          data-testid="cli-instructions-sticky-bottom"
          direction="row"
          spacing={1.5}
          justifyContent="space-between"
          sx={(theme) => ({
            position: 'sticky',
            bottom: 0,
            zIndex: 1,
            pt: 2,
            pb: 0.5,
            backgroundColor: theme.palette.background.paper,
          })}
        >
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<SettingsOutlinedIcon />}
            onClick={() => setIsSetupOpen(true)}
          >
            Set up upload access
          </Button>
          {canCheckForNewUploads ? (
            <Button
              disableElevation
              variant="contained"
              color="primary"
              startIcon={<RefreshOutlinedIcon />}
              data-testid="check-for-new-uploads-button"
              onClick={() => setIsCheckingForNewUploads(true)}
            >
              Check for new uploads
            </Button>
          ) : null}
        </Stack>
      </Box>
      <ItEmailDialog
        open={isItEmailDialogOpen}
        onClose={() => setIsItEmailDialogOpen(false)}
        subject={itEmailSubject}
        body={itEmailBody}
        mailtoHref={mailtoHref}
      />
    </>
  );
};

export default CLIInstructions;
